import React from 'react';
import { CheckCircle, Circle, Warning, Clock } from '@phosphor-icons/react';

interface GoalItem {
    id: string | number;
    goal_id: string;
    title: string;
    status: string;
    progress: number;
    due_date?: string;
    cycle?: string;
    priority?: string;
    value?: number;
}

interface GoalListProps {
    data: GoalItem[] | { data: GoalItem[]; metadata?: any };
    options?: {
        show_progress?: boolean;
        show_due_date?: boolean;
    };
}

const getStatusConfig = (status: string) => {
    const s = status?.toLowerCase() || '';
    if (s.includes('completed') || s.includes('done')) return { color: 'text-green-600 bg-green-50', icon: CheckCircle };
    if (s.includes('risk')) return { color: 'text-red-600 bg-red-50', icon: Warning };
    if (s.includes('progress')) return { color: 'text-blue-600 bg-blue-50', icon: Circle };
    return { color: 'text-gray-600 bg-gray-50', icon: Clock };
};

const getPriorityColor = (priority: string) => {
    const p = priority?.toLowerCase() || '';
    if (p === 'high' || p === 'critical') return 'text-red-600 bg-red-50 border-red-100';
    if (p === 'medium') return 'text-orange-600 bg-orange-50 border-orange-100';
    return 'text-blue-600 bg-blue-50 border-blue-100';
};

export const GoalList: React.FC<GoalListProps> = ({ data }) => {
    // Handle case where data might be wrapped in { data: [...] }
    const items: GoalItem[] = Array.isArray(data) ? data : (data && 'data' in data) ? data.data : [];

    if (!items || items.length === 0) {
        return <div className="flex items-center justify-center h-full text-gray-400 text-sm">No goals found</div>;
    }

    return (
        <div className="flex flex-col gap-3 p-3 h-full overflow-auto bg-white">
            {items.map((item, index) => {
                const statusConfig = getStatusConfig(item.status);
                const StatusIcon = statusConfig.icon;

                return (
                    <div
                        key={`${item.title}-${index}`}
                        className="group relative bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md hover:border-blue-200 transition-all cursor-pointer"
                    >
                        <div className="flex justify-between items-start gap-3 mb-3">
                            <div className="flex-1 min-w-0">
                                <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 group-hover:text-blue-600 transition-colors">
                                    {item.title}
                                </h3>
                                <div className="flex items-center gap-2 mt-1.5">
                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig.color}`}>
                                        <StatusIcon size={12} weight="fill" />
                                        {item.status}
                                    </span>
                                </div>
                            </div>

                            {/* Priority Badge - Hidden on small screens */}
                            {item.priority && (
                                <div className={`hidden sm:flex items-center px-2 py-1 rounded-md border text-xs font-semibold uppercase tracking-wider ${getPriorityColor(item.priority)}`}>
                                    {item.priority}
                                </div>
                            )}
                        </div>

                        <div className="space-y-3">
                            {/* Progress Bar */}
                            <div className="flex items-center gap-3">
                                <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-blue-500 rounded-full transition-all duration-500"
                                        style={{ width: `${item.progress}%` }}
                                    />
                                </div>
                                <span className="text-xs font-medium text-gray-600 min-w-[32px] text-right">
                                    {item.progress}%
                                </span>
                            </div>

                            {/* Footer Info - Cycle hidden on small screens */}
                            {item.cycle && (
                                <div className="hidden sm:flex items-center justify-between pt-3 border-t border-gray-50 text-xs text-gray-500">
                                    <span className="font-medium text-gray-400">Cycle</span>
                                    <span>{item.cycle}</span>
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
